import { useEffect, useState } from 'react'

import InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import Product from '@src/quince-site-common/src/domains/Product'
import productService from '@src/quince-site-common/src/services/productService'
import { captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'

import { InfluencerCardProductDetails } from '../atoms/influencerCard/InfluencerCard'
import { getInitialSelectedOptions, isNestedProduct } from '../molecules/productCard/productCard.helpers'

const FATAL_EXCEPTION_MESSAGE = 'Failed to fetch products in InfluencerSection section'

function buildInfluencerCardProductDetails(product: Product): InfluencerCardProductDetails {
  const selectedOptions = getInitialSelectedOptions(product, [], true, false, isNestedProduct(product) ? 2 : 1)

  return {
    product,
    selectedOptions,
    productTags: product.tags,
  }
}

function getUniqueContentfulProductIds(influencerDetailList: InfluencerDetails[]) {
  return [
    ...new Set(
      influencerDetailList
        .map(influencerDetail => influencerDetail.contentfulProductId)
        .filter((id): id is string => id != null && id !== ''),
    ),
  ]
}

async function fetchInfluencerSectionProducts(
  influencerDetailList: InfluencerDetails[],
  isB2BSelfService?: boolean,
): Promise<Product[]> {
  const spids = !isB2BSelfService
    ? influencerDetailList.map(influencerDetail => influencerDetail.spid).filter(id => id != null)
    : undefined
  const contentfulProductIds = isB2BSelfService ? getUniqueContentfulProductIds(influencerDetailList) : undefined

  if (isB2BSelfService) {
    return (
      (await productService.getProducts({
        contentfulProductIds,
        useLite: true,
      })) ?? []
    )
  }

  let products: Product[] = []
  if (spids?.length) {
    try {
      products = (await productService.getProducts({ spids, useLite: true })) ?? []
    } catch {
      products = []
    }
  }

  const fallbackContentfulProductIds = getUniqueContentfulProductIds(influencerDetailList)
  if (!products.length && fallbackContentfulProductIds.length) {
    products =
      (await productService.getProducts({
        contentfulProductIds: fallbackContentfulProductIds,
        useLite: true,
      })) ?? []
  }

  return products
}

function useInfluencerCardProducts(influencerDetailList: InfluencerDetails[], isB2BSelfService?: boolean) {
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true)
  const [products, setProducts] = useState<Product[]>()
  const [spidToProductMap, setSpidToProductMap] = useState<Record<number, InfluencerCardProductDetails>>({})
  const [contentfulProductIdToProductMap, setContentfulProductIdToProductMap] = useState<
    Record<string, InfluencerCardProductDetails>
  >({})

  useEffect(() => {
    void (async () => {
      try {
        setIsLoadingProducts(true)
        const products = await fetchInfluencerSectionProducts(influencerDetailList, isB2BSelfService)

        if (isB2BSelfService) {
          const contentfulProductIdToProductMap: Record<string, InfluencerCardProductDetails> = {}
          products.forEach(product => {
            contentfulProductIdToProductMap[product.contentfulProductId] = buildInfluencerCardProductDetails(product)
          })
          setContentfulProductIdToProductMap(contentfulProductIdToProductMap)
        } else {
          const productBySpid = Object.fromEntries(products.map(product => [product.spid, product]))
          const productByContentfulId = Object.fromEntries(
            products.map(product => [product.contentfulProductId, product]),
          )
          const spidToProductMap: Record<number, InfluencerCardProductDetails> = {}

          influencerDetailList.forEach(influencerDetail => {
            const product =
              (influencerDetail.spid != null && productBySpid[influencerDetail.spid]) ||
              (influencerDetail.contentfulProductId && productByContentfulId[influencerDetail.contentfulProductId])

            if (!product || influencerDetail.spid == null) return

            spidToProductMap[influencerDetail.spid] = buildInfluencerCardProductDetails(product)
          })

          setSpidToProductMap(spidToProductMap)
        }
        setProducts(products)
      } catch (error) {
        captureFatalException(FATAL_EXCEPTION_MESSAGE, {
          error: JSON.stringify(error),
        })
      } finally {
        setIsLoadingProducts(false)
      }
    })()
  }, [influencerDetailList, isB2BSelfService])

  return {
    isLoadingProducts,
    products,
    spidToProductMap,
    contentfulProductIdToProductMap,
  }
}

export { useInfluencerCardProducts }
